import React, { useState, useEffect, useRef, Suspense, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Canvas, useThree, useFrame } from '@react-three/fiber';
import { OrbitControls, PerspectiveCamera, Environment, Html, useHelper } from '@react-three/drei';
import * as THREE from 'three';
import axios from 'axios';
import html2canvas from 'html2canvas';
import { reportClientError } from '../utils/clientLogger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROOM_COLORS_3D = {
  living_room: '#8BC34A',
  bedroom: '#2196F3',
  kitchen: '#FF9800',
  bathroom: '#00BCD4',
  dining_room: '#FFEB3B',
  office: '#9C27B0',
  hallway: '#9E9E9E',
  garage: '#607D8B',
  laundry: '#03A9F4',
  balcony: '#CDDC39'
};

const FLOOR_ROTATION = [-Math.PI / 2, 0, 0];
const FLOOR_POSITION = [0, 0.01, 0];
const TOP_CAMERA_POSITION = [0, 25, 0];
const PERSPECTIVE_CAMERA_POSITION = [15, 10, 15];
const BED_BASE_POSITION = [0, 0.25, 0];
const BED_MATTRESS_POSITION = [0, 0.55, 0];
const BED_PILLOW_POSITION = [0, 0.7, -0.7];
const BED_HEADBOARD_POSITION = [0, 0.8, -0.95];
const SOFA_BASE_POSITION = [0, 0.2, 0];
const SOFA_BACK_POSITION = [0, 0.55, -0.3];
const SOFA_LEFT_ARM_POSITION = [-0.9, 0.4, 0];
const SOFA_RIGHT_ARM_POSITION = [0.9, 0.4, 0];
const SOFA_LEFT_CUSHION_POSITION = [-0.5, 0.45, 0.1];
const SOFA_RIGHT_CUSHION_POSITION = [0.5, 0.45, 0.1];
const TOILET_BOWL_POSITION = [0, 0.2, 0];
const TOILET_TANK_POSITION = [0, 0.4, -0.25];
const TOILET_SEAT_POSITION = [0, 0.42, 0.05];
const SINK_CABINET_POSITION = [0, 0.4, 0];
const SINK_BASIN_POSITION = [0, 0.82, 0];
const SINK_FAUCET_POSITION = [0, 0.95, -0.15];
const BED_BASE_ARGS = [1.6, 0.5, 2];
const BED_MATTRESS_ARGS = [1.5, 0.2, 1.9];
const BED_PILLOW_ARGS = [1.2, 0.15, 0.4];
const BED_HEADBOARD_ARGS = [1.6, 0.8, 0.1];
const SOFA_BASE_ARGS = [2, 0.4, 0.8];
const SOFA_BACK_ARGS = [2, 0.5, 0.2];
const SOFA_ARM_ARGS = [0.2, 0.4, 0.8];
const SOFA_CUSHION_ARGS = [0.6, 0.1, 0.5];
const TOILET_BOWL_ARGS = [0.2, 0.25, 0.4, 16];
const TOILET_TANK_ARGS = [0.35, 0.4, 0.15];
const TOILET_SEAT_ARGS = [0.18, 0.18, 0.04, 16];
const SINK_CABINET_ARGS = [0.6, 0.8, 0.4];
const SINK_BASIN_ARGS = [0.2, 0.2, 0.08, 16];
const SINK_FAUCET_ARGS = [0.05, 0.2, 0.05];
const DIRECTIONAL_LIGHT_POSITION = [10, 20, 10];
const POINT_LIGHT_POSITION = [-10, 10, -10];
const GROUND_ROTATION = [-Math.PI / 2, 0, 0];
const GROUND_POSITION = [0, -0.01, 0];
const GROUND_PLANE_ARGS = [100, 100];

// Wall component - extruded from 2D
const Wall = ({ start, end, height = 2.5, thickness = 0.15 }) => {
  const length = Math.sqrt(Math.pow(end.x - start.x, 2) + Math.pow(end.y - start.y, 2));
  const angle = Math.atan2(end.y - start.y, end.x - start.x);
  const centerX = (start.x + end.x) / 2;
  const centerZ = (start.y + end.y) / 2;
  const wallPosition = useMemo(() => [centerX, height / 2, centerZ], [centerX, centerZ, height]);
  const wallRotation = useMemo(() => [0, -angle, 0], [angle]);
  const wallArgs = useMemo(() => [length, height, thickness], [length, height, thickness]);

  return (
    <mesh position={wallPosition} rotation={wallRotation}>
      <boxGeometry args={wallArgs} />
      <meshStandardMaterial color="#E8E8E8" roughness={0.8} />
    </mesh>
  );
};

const WallSegment = ({ wall, scale }) => {
  const start = useMemo(
    () => ({ x: wall.start_x / scale, y: wall.start_y / scale }),
    [wall.start_x, wall.start_y, scale]
  );
  const end = useMemo(
    () => ({ x: wall.end_x / scale, y: wall.end_y / scale }),
    [wall.end_x, wall.end_y, scale]
  );

  return <Wall start={start} end={end} />;
};

// Floor component
const Floor = ({ points, color, roomType }) => {
  if (!points || points.length < 3) return null;

  const shape = new THREE.Shape();
  shape.moveTo(points[0].x, points[0].y);
  points.slice(1).forEach(p => shape.lineTo(p.x, p.y));
  shape.closePath();
  const shapeArgs = useMemo(() => [shape], [shape]);

  return (
    <mesh rotation={FLOOR_ROTATION} position={FLOOR_POSITION}>
      <shapeGeometry args={shapeArgs} />
      <meshStandardMaterial color={ROOM_COLORS_3D[roomType] || color || '#CCCCCC'} roughness={0.6} />
    </mesh>
  );
};

// Furniture components
const Bed = ({ position, rotation = 0 }) => {
  const bedRotation = useMemo(() => [0, rotation, 0], [rotation]);

  return (
  <group position={position} rotation={bedRotation}>
    {/* Base */}
    <mesh position={BED_BASE_POSITION}>
      <boxGeometry args={BED_BASE_ARGS} />
      <meshStandardMaterial color="#8B4513" />
    </mesh>
    {/* Mattress */}
    <mesh position={BED_MATTRESS_POSITION}>
      <boxGeometry args={BED_MATTRESS_ARGS} />
      <meshStandardMaterial color="#FFFFFF" />
    </mesh>
    {/* Pillow */}
    <mesh position={BED_PILLOW_POSITION}>
      <boxGeometry args={BED_PILLOW_ARGS} />
      <meshStandardMaterial color="#F5F5DC" />
    </mesh>
    {/* Headboard */}
    <mesh position={BED_HEADBOARD_POSITION}>
      <boxGeometry args={BED_HEADBOARD_ARGS} />
      <meshStandardMaterial color="#8B4513" />
    </mesh>
  </group>
  );
};

const Sofa = ({ position, rotation = 0 }) => {
  const sofaRotation = useMemo(() => [0, rotation, 0], [rotation]);

  return (
  <group position={position} rotation={sofaRotation}>
    {/* Base */}
    <mesh position={SOFA_BASE_POSITION}>
      <boxGeometry args={SOFA_BASE_ARGS} />
      <meshStandardMaterial color="#4A4A4A" />
    </mesh>
    {/* Back */}
    <mesh position={SOFA_BACK_POSITION}>
      <boxGeometry args={SOFA_BACK_ARGS} />
      <meshStandardMaterial color="#4A4A4A" />
    </mesh>
    {/* Left arm */}
    <mesh position={SOFA_LEFT_ARM_POSITION}>
      <boxGeometry args={SOFA_ARM_ARGS} />
      <meshStandardMaterial color="#4A4A4A" />
    </mesh>
    {/* Right arm */}
    <mesh position={SOFA_RIGHT_ARM_POSITION}>
      <boxGeometry args={SOFA_ARM_ARGS} />
      <meshStandardMaterial color="#4A4A4A" />
    </mesh>
    {/* Cushions */}
    <mesh position={SOFA_LEFT_CUSHION_POSITION}>
      <boxGeometry args={SOFA_CUSHION_ARGS} />
      <meshStandardMaterial color="#6B6B6B" />
    </mesh>
    <mesh position={SOFA_RIGHT_CUSHION_POSITION}>
      <boxGeometry args={SOFA_CUSHION_ARGS} />
      <meshStandardMaterial color="#6B6B6B" />
    </mesh>
  </group>
  );
};

const Table = ({ position, rotation = 0, type = 'dining' }) => {
  const tableColor = type === 'kitchen' ? '#CCCCCC' : '#8B4513';
  const height = type === 'kitchen' ? 0.9 : 0.75;
  const width = type === 'kitchen' ? 2 : 1.5;
  const depth = type === 'kitchen' ? 0.6 : 0.9;
  const tableRotation = useMemo(() => [0, rotation, 0], [rotation]);
  const tableTopPosition = useMemo(() => [0, height, 0], [height]);
  const tableTopArgs = useMemo(() => [width, 0.05, depth], [width, depth]);
  const legPositions = useMemo(
    () => [
      [-width / 2 + 0.05, height / 2, -depth / 2 + 0.05],
      [width / 2 - 0.05, height / 2, -depth / 2 + 0.05],
      [-width / 2 + 0.05, height / 2, depth / 2 - 0.05],
      [width / 2 - 0.05, height / 2, depth / 2 - 0.05],
    ],
    [width, depth, height]
  );
  const legGeometryArgs = useMemo(() => [0.05, height, 0.05], [height]);

  return (
    <group position={position} rotation={tableRotation}>
      {/* Top */}
      <mesh position={tableTopPosition}>
        <boxGeometry args={tableTopArgs} />
        <meshStandardMaterial color={tableColor} />
      </mesh>
      {/* Legs */}
      {legPositions.map((legPosition) => (
        <mesh key={`leg-${legPosition[0]}-${legPosition[2]}`} position={legPosition}>
          <boxGeometry args={legGeometryArgs} />
          <meshStandardMaterial color={tableColor} />
        </mesh>
      ))}
    </group>
  );
};

const Toilet = ({ position, rotation = 0 }) => {
  const toiletRotation = useMemo(() => [0, rotation, 0], [rotation]);

  return (
  <group position={position} rotation={toiletRotation}>
    {/* Bowl */}
    <mesh position={TOILET_BOWL_POSITION}>
      <cylinderGeometry args={TOILET_BOWL_ARGS} />
      <meshStandardMaterial color="#FFFFFF" />
    </mesh>
    {/* Tank */}
    <mesh position={TOILET_TANK_POSITION}>
      <boxGeometry args={TOILET_TANK_ARGS} />
      <meshStandardMaterial color="#FFFFFF" />
    </mesh>
    {/* Seat */}
    <mesh position={TOILET_SEAT_POSITION}>
      <cylinderGeometry args={TOILET_SEAT_ARGS} />
      <meshStandardMaterial color="#F5F5F5" />
    </mesh>
  </group>
  );
};

const Sink = ({ position, rotation = 0 }) => {
  const sinkRotation = useMemo(() => [0, rotation, 0], [rotation]);

  return (
  <group position={position} rotation={sinkRotation}>
    {/* Cabinet */}
    <mesh position={SINK_CABINET_POSITION}>
      <boxGeometry args={SINK_CABINET_ARGS} />
      <meshStandardMaterial color="#FFFFFF" />
    </mesh>
    {/* Basin */}
    <mesh position={SINK_BASIN_POSITION}>
      <cylinderGeometry args={SINK_BASIN_ARGS} />
      <meshStandardMaterial color="#EEEEEE" />
    </mesh>
    {/* Faucet */}
    <mesh position={SINK_FAUCET_POSITION}>
      <boxGeometry args={SINK_FAUCET_ARGS} />
      <meshStandardMaterial color="#C0C0C0" metalness={0.8} roughness={0.2} />
    </mesh>
  </group>
  );
};

// Room with auto-furniture
const RoomWithFurniture = ({ room, scale }) => {
  if (!room || !room.points || room.points.length < 3) return null;

  const points3D = useMemo(
    () => room.points.map((p) => ({ x: p.x / scale, y: p.y / scale })),
    [room, scale]
  );
  const centerX = useMemo(() => points3D.reduce((sum, p) => sum + p.x, 0) / points3D.length, [points3D]);
  const centerZ = useMemo(() => points3D.reduce((sum, p) => sum + p.y, 0) / points3D.length, [points3D]);
  const centerPosition = useMemo(() => [centerX, 0, centerZ], [centerX, centerZ]);
  const toiletPosition = useMemo(() => [centerX - 0.5, 0, centerZ], [centerX, centerZ]);
  const sinkPosition = useMemo(() => [centerX + 0.5, 0, centerZ], [centerX, centerZ]);
  const rotation = useMemo(() => Math.random() * Math.PI * 2, [room.id, room.room_type]);
  const furnitureConfig = useMemo(
    () => ({ centerPosition, toiletPosition, sinkPosition, rotation }),
    [centerPosition, toiletPosition, sinkPosition, rotation]
  );

  const furniture = useMemo(() => {
    switch (room.room_type) {
      case 'bedroom':
        return [
          <Bed
            key={`bed-${room.id || 'bedroom'}`}
            position={furnitureConfig.centerPosition}
            rotation={furnitureConfig.rotation}
          />
        ];
      case 'living_room':
        return [
          <Sofa
            key={`sofa-${room.id || 'living'}`}
            position={furnitureConfig.centerPosition}
            rotation={furnitureConfig.rotation}
          />
        ];
      case 'kitchen':
        return [
          <Table
            key={`counter-${room.id || 'kitchen'}`}
            position={furnitureConfig.centerPosition}
            rotation={furnitureConfig.rotation}
            type="kitchen"
          />
        ];
      case 'bathroom':
        return [
          <Toilet
            key={`toilet-${room.id || 'bathroom'}`}
            position={furnitureConfig.toiletPosition}
            rotation={furnitureConfig.rotation}
          />,
          <Sink
            key={`sink-${room.id || 'bathroom'}`}
            position={furnitureConfig.sinkPosition}
            rotation={furnitureConfig.rotation}
          />
        ];
      case 'dining_room':
        return [
          <Table
            key={`table-${room.id || 'dining'}`}
            position={furnitureConfig.centerPosition}
            rotation={furnitureConfig.rotation}
            type="dining"
          />
        ];
      default:
        return [];
    }
  }, [room.room_type, room.id, furnitureConfig]);

  return (
    <>
      <Floor points={points3D} color={room.color} roomType={room.room_type} />
      {furniture}
    </>
  );
};

// Main scene
const Scene = ({ floorPlan, viewMode, showFurniture }) => {
  if (!floorPlan) return null;

  const scale = floorPlan.scale || 20;
  const floorWidth = (floorPlan.width || 800) / scale;
  const floorHeight = (floorPlan.height || 600) / scale;
  const centerX = floorWidth / 2;
  const centerZ = floorHeight / 2;
  const groupPosition = useMemo(() => [-centerX, 0, -centerZ], [centerX, centerZ]);
  const floorPosition = useMemo(() => [centerX, 0, centerZ], [centerX, centerZ]);
  const planeArgs = useMemo(() => [floorWidth, floorHeight], [floorWidth, floorHeight]);

  return (
    <group position={groupPosition}>
      {/* Main floor */}
      <mesh rotation={FLOOR_ROTATION} position={floorPosition}>
        <planeGeometry args={planeArgs} />
        <meshStandardMaterial color="#D7CCC8" roughness={0.8} />
      </mesh>

      {/* Rooms */}
      {floorPlan.rooms?.map((room) => (
        <RoomWithFurniture key={room.id || `${room.room_type}-${room.points?.map((point) => `${point.x}-${point.y}`).join('_')}`} room={room} scale={scale} />
      ))}

      {/* Walls */}
      {floorPlan.walls?.map((wall) => (
        <WallSegment
          key={wall.id || `${wall.start_x}-${wall.start_y}-${wall.end_x}-${wall.end_y}`}
          wall={wall}
          scale={scale}
        />
      ))}
    </group>
  );
};

// Camera controller
const CameraController = ({ viewMode, target }) => {
  const { camera } = useThree();

  useEffect(() => {
    if (viewMode === 'top') {
      camera.position.set(...TOP_CAMERA_POSITION);
      camera.lookAt(0, 0, 0);
    } else {
      camera.position.set(...PERSPECTIVE_CAMERA_POSITION);
      camera.lookAt(0, 0, 0);
    }
  }, [viewMode, camera]);

  return null;
};

// Loading component
const Loading = () => (
  <Html center>
    <div className="text-white text-center">
      <div className="w-12 h-12 border-4 border-white/30 border-t-white rounded-full animate-spin mx-auto mb-4"></div>
      <p>Carregando modelo 3D...</p>
    </div>
  </Html>
);

const Render3D = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState('perspective'); // 'perspective' or 'top'
  const [showFurniture, setShowFurniture] = useState(true);
  const [exporting, setExporting] = useState(false);
  const loadProject = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}`);
      setProject(response.data);
      await axios.post(`${API}/projects/${projectId}/3d-view`);
    } catch {
      setError('Projeto não encontrado');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadProject();
  }, [loadProject]);

  const exportImage = async () => {
    if (!containerRef.current) return;

    setExporting(true);
    try {
      const canvas = containerRef.current.querySelector('canvas');
      if (canvas) {
        const link = document.createElement('a');
        link.download = `${project?.name || 'render-3d'}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
      }
    } catch (err) {
      reportClientError('Error exporting image:', err);
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white text-lg">Carregando visualização 3D...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">❌</div>
          <p className="text-white text-lg mb-4" data-testid="render-3d-error-message">{error}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            data-testid="render-3d-back-dashboard-button"
          >
            Voltar ao Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/80 to-transparent">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="text-white/80 hover:text-white"
              data-testid="render-3d-header-back-button"
            >
              ← Voltar
            </button>
            <div>
              <h1 className="text-xl font-bold text-white" data-testid="render-3d-project-name">{project?.name}</h1>
              <p className="text-white/60 text-sm">Visualização 3D</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={exportImage}
              disabled={exporting}
              className="px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition flex items-center gap-2"
              data-testid="render-3d-export-button"
            >
              📷 {exporting ? 'Exportando...' : 'Exportar PNG'}
            </button>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex items-center gap-3 bg-black/60 backdrop-blur-sm rounded-full px-6 py-3">
        <button
          onClick={() => setViewMode('perspective')}
          className={`px-4 py-2 rounded-full transition ${viewMode === 'perspective' ? 'bg-blue-600 text-white' : 'text-white/70 hover:text-white'}`}
          data-testid="render-3d-view-perspective-button"
        >
          🎥 Perspectiva
        </button>
        <button
          onClick={() => setViewMode('top')}
          className={`px-4 py-2 rounded-full transition ${viewMode === 'top' ? 'bg-blue-600 text-white' : 'text-white/70 hover:text-white'}`}
          data-testid="render-3d-view-top-button"
        >
          📐 Vista Topo
        </button>
        <div className="w-px h-6 bg-white/20"></div>
        <button
          onClick={() => setShowFurniture(!showFurniture)}
          className={`px-4 py-2 rounded-full transition ${showFurniture ? 'bg-green-600 text-white' : 'text-white/70 hover:text-white'}`}
          data-testid="render-3d-toggle-furniture-button"
        >
          🛋️ Mobília
        </button>
      </div>

      {/* Instructions */}
      <div className="absolute top-24 right-6 z-10 bg-black/60 backdrop-blur-sm rounded-xl p-4 text-white/80 text-sm max-w-xs">
        <h3 className="font-semibold text-white mb-2">Controles</h3>
        <ul className="space-y-1">
          <li>🖱️ Arrastar: Rotacionar</li>
          <li>🔄 Scroll: Zoom</li>
          <li>📍 Shift + Arrastar: Mover</li>
        </ul>
      </div>

      {/* 3D Canvas */}
      <div ref={containerRef} className="w-full h-screen">
        <Canvas shadows>
          <Suspense fallback={<Loading />}>
            <CameraController viewMode={viewMode} />
            <PerspectiveCamera makeDefault position={PERSPECTIVE_CAMERA_POSITION} fov={50} />
            <OrbitControls
              enablePan={true}
              enableZoom={true}
              enableRotate={viewMode === 'perspective'}
              maxPolarAngle={viewMode === 'top' ? 0 : Math.PI / 2}
              minDistance={5}
              maxDistance={50}
            />

            {/* Lighting */}
            <ambientLight intensity={0.5} />
            <directionalLight
              position={DIRECTIONAL_LIGHT_POSITION}
              intensity={1}
              castShadow
              shadow-mapSize-width={2048}
              shadow-mapSize-height={2048}
            />
            <pointLight position={POINT_LIGHT_POSITION} intensity={0.3} />

            {/* Environment */}
            <Environment preset="apartment" />

            {/* Scene */}
            <Scene
              floorPlan={project?.floor_plan}
              viewMode={viewMode}
              showFurniture={showFurniture}
            />

            {/* Ground plane */}
            <mesh rotation={GROUND_ROTATION} position={GROUND_POSITION} receiveShadow>
              <planeGeometry args={GROUND_PLANE_ARGS} />
              <meshStandardMaterial color="#1a1a2e" />
            </mesh>
          </Suspense>
        </Canvas>
      </div>

      {/* Project Info Panel */}
      <div className="absolute top-24 left-6 z-10 bg-black/60 backdrop-blur-sm rounded-xl p-4 text-white max-w-xs">
        <h3 className="font-semibold mb-2" data-testid="render-3d-info-title">📊 Informações</h3>
        <div className="space-y-1 text-sm text-white/80">
          <p data-testid="render-3d-info-rooms">Cômodos: {project?.floor_plan?.rooms?.length || 0}</p>
          <p data-testid="render-3d-info-walls">Paredes: {project?.floor_plan?.walls?.length || 0}</p>
          <p data-testid="render-3d-info-views">Visualizações 3D: {project?.view_count_3d || 0}</p>
        </div>
        <button
          onClick={() => navigate(`/desenhar-planta`)}
          className="mt-4 w-full py-2 bg-blue-600 rounded-lg text-sm hover:bg-blue-700 transition"
          data-testid="render-3d-edit-floorplan-button"
        >
          ✏️ Editar Planta
        </button>
      </div>
    </div>
  );
};

export default Render3D;
